import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from 'src/@/components/ui/dialog';
import Scrollbar from 'src/components/Scrollbar';
import classNames from 'src/utils/classNames';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { useSelector } from 'src/redux/store';
import { useDispatch } from 'react-redux';
import { getChatInternoBuscarChat, getChatInternoBuscarContatos, setSelectedChat } from 'src/redux/slices/staff';
import useAuth from 'src/hooks/useAuth';
import { useNavigate } from 'react-router';
import v2_actionChatInternoCriarGrupo from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoCriarGrupo';
import v2_actionChatInternoEditarGrupo from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoEditarGrupo';
import v2_actionChatInternoCriar from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoCriar';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import { newChatDesign as d } from './NewChat.design';

const getContactLabel = (contact) =>
  contact?.nomeFantasia || contact?.firstName || contact?.email || '?';

const getContactInitial = (contact) =>
  getContactLabel(contact).charAt(0).toUpperCase();

const NewChat = ({ isEdit, icon, listaChats, onCallback, customTrigger }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cliente, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState([]);
  const [query, setQuery] = useState('');
  const [groupName, setGroupName] = useState('');
  const { listaContatos, staff } = useSelector((state) => state.staff);
  const [loading, setLoading] = useState(false);
  const selectedCount = selectedContact?.length || 0;
  const isGroupMode = selectedCount > 1;

  // Admin do grupo (primeiro participante)
  const admin = useMemo(() => {
    if (isEdit && staff?.isGroup && staff.people?.length > 0) {
      return staff.people[0];
    }
    return null;
  }, [isEdit, staff]);

  const contacts = useMemo(() => {
    if (!listaContatos) return [];
    const searchLower = query?.toLowerCase() || '';
    return listaContatos
      .filter((contact) => {
        if (!searchLower) return true;
        const nomeFantasia = contact?.nomeFantasia?.toLowerCase() || '';
        const email = contact?.email?.toLowerCase() || '';
        return nomeFantasia.includes(searchLower) || email.includes(searchLower);
      })
      .filter((contact) => {
        if (user && contact._id === user.id) {
          return false;
        }
        if (admin && contact._id === admin._id) {
          return false;
        }
        return true;
      });
  }, [listaContatos, query, user, admin]);

  useEffect(() => {
    if (isEdit && staff?.isGroup) {
      setSelectedContact(staff.people);
      setGroupName(staff.title);
    }
  }, [staff, open]);

  const handleDialogClose = (nextOpen) => {
    const willOpen = typeof nextOpen === 'boolean' ? nextOpen : !open;
    setOpen(willOpen);
    if (!willOpen) {
      setSelectedContact([]);
      setQuery('');
      setGroupName('');
    }
  };

  const openDialog = () => {
    setOpen(true);
    dispatch(getChatInternoBuscarContatos(cliente?._id));
  };

  const handleStartNewChat = async () => {
    if (selectedContact.length === 0) return alert(t('chat.internal.selectContactToStart'));
    if (
      selectedContact.some((contact) =>
        listaChats.some((chat) => !chat.isGroup && chat.people.some((p) => p._id === contact._id))
      )
    ) {
      const existingChat = listaChats.find(
        (chat) => !chat.isGroup && chat.people.some((p) => p._id === selectedContact[0]._id)
      );
      if (existingChat) {
        setOpen(false);
        setSelectedContact([]);
        dispatch(getChatInternoBuscarChat(existingChat._id));
        dispatch(setSelectedChat(existingChat));
        navigate(`/chats/interno/${existingChat._id}`);
        onCallback();
      }
      return;
    }

    try {
      setLoading(true);
      const response = await v2_actionChatInternoCriar(cliente?._id, {
        counterpart: selectedContact[0]._id,
        user_id: user.id,
      });
      if (response.status === 200) {
        setOpen(false);
        setSelectedContact([]);
        dispatch(getChatInternoBuscarChat(response.data.room._id));
        dispatch(setSelectedChat(response.data.room));
        navigate(`/chats/interno/${response.data.room._id}`);
        handleDialogClose(false);
        onCallback();
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleSave = () => {
    if (selectedContact?.length === 1 && staff.isGroup && isEdit)
      return alert(t('chat.internal.groupMustHaveMoreThanOne'));
    alert(t('chat.internal.updateChat'));
  };

  const handleSaveGroup = async () => {
    try {
      let peoples = selectedContact.map((contact) => contact._id);
      if (isEdit && staff?.isGroup && staff.people?.length > 0) {
        const adminId = staff.people[0]._id;
        if (!peoples.includes(adminId)) {
          peoples = [adminId, ...peoples];
        }
      }
      let scope = {
        people: peoples,
        title: groupName,
      };
      let data = { scope };
      setLoading(true);
      const response = await v2_actionChatInternoEditarGrupo(staff._id, data);
      if (response.status === 200) {
        setOpen(false);
        setSelectedContact([]);
        setGroupName('');
        dispatch(getChatInternoBuscarChat(response.data.data._id));
        dispatch(setSelectedChat(response.data.data));
        navigate(`/chats/interno/${response.data.data._id}`);
        setLoading(false);
        onCallback();
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    try {
      setLoading(true);
      const response = await v2_actionChatInternoCriarGrupo(cliente?._id, {
        user_id: user.id,
        people: selectedContact,
        title: groupName,
      });
      if (response.status === 200) {
        setOpen(false);
        setSelectedContact([]);
        setGroupName('');
        dispatch(getChatInternoBuscarChat(response.data.data._id));
        dispatch(setSelectedChat(response.data.data));
        navigate(`/chats/interno/${response.data.data._id}`);
        setLoading(false);
        onCallback();
        handleDialogClose(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleSetSelectedContact = (contact) => {
    if (admin && contact._id === admin._id) {
      return;
    }

    setSelectedContact((prevSelectedContacts) => {
      if (!Array.isArray(prevSelectedContacts)) {
        prevSelectedContacts = [];
      }
      const contactExists = prevSelectedContacts.some((c) => c._id === contact._id);

      if (contactExists) {
        if (admin && contact._id === admin._id) {
          return prevSelectedContacts;
        }
        const filtered = prevSelectedContacts.filter((c) => c._id !== contact._id);
        if (admin && !filtered.some((c) => c._id === admin._id)) {
          return [admin, ...filtered];
        }
        return filtered;
      }

      const updated = [...prevSelectedContacts, contact];
      if (admin && !updated.some((c) => c._id === admin._id)) {
        return [admin, ...updated];
      }
      if (admin && updated.some((c) => c._id === admin._id)) {
        const withoutAdmin = updated.filter((c) => c._id !== admin._id);
        return [admin, ...withoutAdmin];
      }
      return updated;
    });
  };

  const primaryDisabled = selectedCount === 0 || (isGroupMode && !groupName);
  const primaryLabel = isEdit
    ? isGroupMode
      ? t('chat.internal.updateGroup')
      : t('chat.internal.updateChat')
    : isGroupMode
      ? t('chat.internal.createGroup')
      : t('chat.internal.createChat');
  const primaryAction = isEdit
    ? isGroupMode
      ? handleSaveGroup
      : handleSave
    : isGroupMode
      ? handleCreateGroup
      : handleStartNewChat;

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogTrigger asChild>
        {customTrigger ? (
          <div onClick={openDialog}>{customTrigger}</div>
        ) : isEdit ? (
          <Button size="icon" variant="outline" onClick={openDialog}>
            <Iconify icon="ic:baseline-edit" />
          </Button>
        ) : icon ? (
          <Button
            className="flex items-center gap-2 h-7 bg-slate-700 dark:bg-[#383838]"
            onClick={openDialog}
          >
            <Iconify icon="fluent:chat-add-24-regular" />
            {t('chat.internal.newChat')}
          </Button>
        ) : (
          <Button className="w-full bg-violet-600" onClick={openDialog}>
            {t('chat.internal.newChat')}
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className={d.dialog}>
        <div className={d.header}>
          <h2 className={d.title}>
            {isEdit ? t('chat.internal.updateGroup') : t('chat.internal.newChatTitle')}
          </h2>
          <p className={d.subtitle}>
            {isEdit
              ? t('chat.internal.selectPeopleHint')
              : t('chat.internal.newChatSubtitle')}
          </p>
          {!isEdit && selectedCount > 0 && (
            <p className={d.status}>
              {isGroupMode
                ? t('chat.internal.creatingGroupWith', { count: selectedCount })
                : t('chat.internal.creatingIndividualChat', {
                    name: getContactLabel(selectedContact[0]),
                  })}
            </p>
          )}
        </div>

        <div className={d.body}>
          {selectedCount > 0 && (
            <div className={d.selectedWrap}>
              <div className={d.selectedHeader}>
                <span className={d.selectedLabel}>{t('chat.internal.selectedContacts')}</span>
                <span className={d.selectedCount}>
                  {t('chat.internal.selectedCount', { count: selectedCount })}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedContact.map((contact) => {
                  const isAdmin = admin && contact._id === admin._id;
                  return (
                    <button
                      key={contact._id}
                      type="button"
                      onClick={() => handleSetSelectedContact(contact)}
                      disabled={isAdmin}
                      className={classNames(d.chip, isAdmin && d.chipDisabled)}
                      title={isAdmin ? t('chat.internal.adminTag') : undefined}
                    >
                      <span className="max-w-[110px] truncate">{getContactLabel(contact)}</span>
                      {!isAdmin && <span aria-hidden="true">×</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className={d.searchWrap}>
            <Input
              placeholder={t('chat.internal.searchPlaceholder')}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={d.searchInput}
            />
          </div>

          {admin && (
            <div className="mb-3">
              <p className={d.listLabel}>{t('chat.internal.groupAdmin')}</p>
              <div className={classNames(d.contactRow, d.contactRowSelected, 'cursor-not-allowed opacity-80')}>
                <div className={classNames(d.avatar, d.avatarSelected)}>
                  {getContactInitial(admin)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={d.contactName}>
                    {getContactLabel(admin)}
                    <span className="ml-1.5 text-[10px] font-medium text-violet-500">
                      ({t('chat.internal.adminTag')})
                    </span>
                  </p>
                  {admin.email && <p className={d.contactEmail}>{admin.email}</p>}
                </div>
                <div className={d.checkBadge}>
                </div>
              </div>
            </div>
          )}

          <p className={d.listLabel}>
            {t('chat.internal.contacts')} · {contacts.length}
          </p>

          <Scrollbar className="overflow-y-auto h-[280px]">
            <div className="pr-1 flex flex-col gap-0.5 pb-2">
              {contacts.length === 0 ? (
                <div className={d.empty}>
                  <p className="text-sm font-medium text-gray-400">
                    {query
                      ? t('chat.internal.noUsersAvailable', { searchText: query })
                      : t('chat.internal.empty')}
                  </p>
                </div>
              ) : (
                contacts.map((contact) => {
                  const isSelected = selectedContact?.some((item) => item._id === contact._id);
                  return (
                    <div
                      key={contact._id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (contact._id === user.id && isEdit) return;
                        handleSetSelectedContact(contact);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSetSelectedContact(contact);
                        }
                      }}
                      className={classNames(
                        d.contactRow,
                        d.contactRowHover,
                        isSelected && d.contactRowSelected,
                        contact._id === user._id && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <div
                        className={classNames(
                          d.avatar,
                          isSelected ? d.avatarSelected : d.avatarIdle
                        )}
                      >
                        {getContactInitial(contact)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={d.contactName}>{getContactLabel(contact)}</p>
                        {contact.email &&
                          contact.email !== getContactLabel(contact) && (
                            <p className={d.contactEmail}>{contact.email}</p>
                          )}
                      </div>
                      {isSelected ? (
                        <div className={d.checkBadge} />
                      ) : (
                        <div className={d.checkEmpty} />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Scrollbar>

          {isGroupMode && (
            <div className={d.groupNameWrap}>
              <p className={d.groupNameLabel}>{t('chat.internal.groupName')}</p>
              <Input
                placeholder={t('chat.internal.groupNamePlaceholder')}
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className={d.groupNameInput}
              />
            </div>
          )}
        </div>

        <div className={d.footer}>
          <p className="text-xs text-gray-500 dark:text-neutral-400 leading-4">
            {!isEdit && selectedCount === 1
              ? t('chat.internal.selectMoreForGroup')
              : isGroupMode
                ? t('chat.internal.creatingGroupWith', { count: selectedCount })
                : t('chat.internal.selectOneForChat')}
          </p>
          <ButtonLoading
            loading={loading}
            disabled={primaryDisabled}
            className={d.primaryBtn}
            onClick={primaryAction}
          >
            {primaryLabel}
          </ButtonLoading>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewChat;
